import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { Loader2, RefreshCw } from 'lucide-react';
import { useStore } from '../../../store';
import {
  cancelPatientExternalEmrImportAndPublish,
  refreshPatientExternalEmrImport,
  startPatientExternalEmrImports,
  useExternalEmrCardStatus,
} from '../../../hooks/usePatientExternalEmrImport';
import {
  canRefreshFromStatus,
  canStartFromStatus,
  completedRefreshResult,
  historyPending,
  isMovingStatus,
  type ExternalEmrCardStatus,
  type ExternalEmrPatientState,
} from '../../../services/externalEmrPatients';
import { useNow } from '../../../hooks/useNow';
import { useTakeOverAvailable } from '../../../hooks/useTakeOverAvailable';
import {
  importStageLabel,
  importTimeLeftLine,
} from '../../../services/externalEmrImportStatus';
import { getBusinessErrorMessage } from '../../../utils/apiErrorDetail';
import {
  formatDateInAgencyZone,
  formatDateToMMDDYYYY,
} from '../../../utils/dateUtils';
import {
  CANCEL_IMPORT_ERROR,
  CANCEL_IMPORT_LABEL,
  IMPORT_FAILED_FALLBACK,
  RETRY_IMPORT_LABEL,
  queuePositionLine,
  ADD_RECERT_EPISODE_LABEL,
  cardStatusHelp,
  cardStatusPillLabel,
  CONFIRM_START_ACCEPT,
  CUTOVER_CONFIRM_BODY,
  CUTOVER_CONFIRM_TITLE,
  HISTORY_ARRIVING_REFRESH_LINE,
  IMPORT_QUEUED_TOAST,
  midEpisodeConfirmBody,
  MID_EPISODE_CONFIRM_TITLE,
  START_IMPORT_ERROR,
  START_IMPORT_LABEL,
  REFRESH_CHART_ERROR,
  REFRESH_CHART_HELP,
  REFRESH_CHART_LABEL,
  REFRESH_CHART_SHORT_LABEL,
  importedOnLine,
  lastRefreshedLine,
  REFRESH_FAILED_FALLBACK,
  REFRESH_QUEUED_TOAST,
  keptLinesMoreLine,
  refreshResultLine,
} from './externalEmrPatientCopy';

const PILL_TONE: Record<ExternalEmrCardStatus, string> = {
  ready: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  mid_episode: 'bg-gray-50 text-gray-800 border-gray-200',
  ending_soon: 'bg-gray-50 text-gray-800 border-gray-200',
  ready_to_cut_over: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  left_before_end: 'bg-transparent text-gray-600 border-dashed border-gray-300',
  queued: 'bg-blue-50 text-blue-800 border-blue-200',
  importing: 'bg-blue-50 text-blue-800 border-blue-200',
  failed: 'bg-red-50 text-red-800 border-red-200',
  imported: 'bg-green-50 text-green-800 border-green-200',
  refreshing: 'bg-blue-50 text-blue-800 border-blue-200',
  refresh_failed: 'bg-red-50 text-red-800 border-red-200',
};

/**
 * The status pill. It may wrap: the longest census labels ("Left the census
 * before the episode ended") are wider than a 16rem card, and a wrapped
 * rectangle reads better than a pill that spills past the card edge.
 */
export const ExternalEmrStatusPill: React.FC<{
  status: ExternalEmrCardStatus;
  /** The pill's words; defaults to the status name. */
  label?: string;
}> = ({ status, label }) => {
  const takeOverV2 = useTakeOverAvailable();
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 rounded border px-1.5 py-0.5 text-left text-xs font-medium ${PILL_TONE[status]}`}
      title={cardStatusHelp(status, takeOverV2)}
      data-testid='external-emr-status-pill'
    >
      {isMovingStatus(status) && (
        <Loader2 className='h-3 w-3 animate-spin' aria-hidden='true' />
      )}
      {label ?? cardStatusPillLabel(status, {})}
    </span>
  );
};

interface ExternalEmrImportControlsProps {
  patientId: string;
  external: ExternalEmrPatientState;
  /** Render gate: `chart-import:start` and an agency that can import. */
  canStart: boolean;
  /** Card or chart page. The page uses larger text and no chart link. */
  variant?: 'card' | 'page';
  /** Chart bar: sit on the parent row. Messages wrap; cards stay stacked. */
  layout?: 'stack' | 'inline';
  /** An import settled or a start/cancel landed: reload counts and lists. */
  onChanged?: () => void;
  /** Shown when the imported chart's last episode has ended. */
  recertHref?: string | null;
  onAddRecertEpisode?: () => void;
  /** When the list request carrying `external.import` began (ms). */
  seedAt?: number | null;
  /**
   * Show the Imported pill. The card turns it off: its name row already
   * wears the Imported badge.
   */
  showImportedPill?: boolean;
  /**
   * The chart's first import, when the live status is a newer refresh and
   * no longer carries it. The patient row keeps this for the life of the chart.
   */
  importedAt?: string | null;
  /**
   * Why the agency cannot import now (its External EMR login or consent).
   * Shown in place of Import, Retry and Refresh, so nobody presses to find out.
   */
  blockedReason?: string | null;
  /**
   * Hide Import, Retry and Refresh without a reason line, where the page says
   * why once (the Patient Manager header). Cancel stays: it starts nothing.
   */
  startBlocked?: boolean;
}

/**
 * Status pill, progress line and the one next action for an External EMR
 * patient. One click starts a Ready shell; a Mid-episode start asks first,
 * because the chart stops following External EMR the moment it is copied.
 *
 * An imported chart offers Refresh from External EMR instead: it brings the
 * chart forward and keeps every row edited in Roger. Progress reads like an
 * import; the result says what changed and what was kept.
 */
export const ExternalEmrImportControls: React.FC<
  ExternalEmrImportControlsProps
> = ({
  patientId,
  external,
  canStart,
  variant = 'card',
  layout = 'stack',
  onChanged,
  recertHref,
  onAddRecertEpisode,
  seedAt = null,
  showImportedPill = true,
  importedAt = null,
  blockedReason = null,
  startBlocked = false,
}) => {
  const showError = useStore(state => state.showError);
  const showSuccess = useStore(state => state.showSuccess);
  const takeOverV2 = useTakeOverAvailable();
  const { live, status } = useExternalEmrCardStatus(
    patientId,
    external,
    seedAt
  );
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const running = status === 'importing' || status === 'refreshing';
  const now = useNow(running);
  const timeLeft = running ? importTimeLeftLine(live, now) : null;

  // Tell the page once when an import settles, so counts follow the card.
  const previous = useRef(live.state);
  useEffect(() => {
    const was = previous.current;
    previous.current = live.state;
    const settled =
      live.state === 'completed' ||
      live.state === 'failed' ||
      live.state === 'canceled';
    if (settled && (was === 'queued' || was === 'running')) onChanged?.();
  }, [live.state, onChanged]);

  const start = async () => {
    setBusy(true);
    try {
      const [row] = await startPatientExternalEmrImports([patientId]);
      if (!row || row.refused_reason) {
        // The backend writes refused_reason for users, in the chart
        // register; fall back to the generic line when it sent none.
        console.error('External EMR import refused:', row?.refused_reason);
        showError(row?.refused_reason || START_IMPORT_ERROR);
      } else {
        showSuccess(IMPORT_QUEUED_TOAST);
        onChanged?.();
      }
    } catch (error) {
      console.error('Failed to start the External EMR import:', error);
      showError(getBusinessErrorMessage(error) ?? START_IMPORT_ERROR);
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const row = await refreshPatientExternalEmrImport(patientId);
      if (!row || row.refused_reason) {
        console.error('External EMR refresh refused:', row?.refused_reason);
        showError(row?.refused_reason || REFRESH_CHART_ERROR);
        // The history started arriving since this page read it: re-read.
        if (row?.refused_code === 'history_arriving') onChanged?.();
      } else {
        showSuccess(REFRESH_QUEUED_TOAST);
        onChanged?.();
      }
    } catch (error) {
      console.error('Failed to start the External EMR refresh:', error);
      showError(getBusinessErrorMessage(error) ?? REFRESH_CHART_ERROR);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await cancelPatientExternalEmrImportAndPublish(patientId);
      onChanged?.();
    } catch (error) {
      console.error('Failed to cancel the External EMR import:', error);
      showError(getBusinessErrorMessage(error) ?? CANCEL_IMPORT_ERROR);
    } finally {
      setBusy(false);
    }
  };

  // The External EMR already built the next period: the confirm names the
  // next step (Add Episode) instead of warning about an open episode.
  const cutover = external.census_status === 'recertified_in_external_emr';
  const requestStart = () => {
    if (external.ready && !cutover) {
      void start();
    } else {
      setConfirmOpen(true);
    }
  };

  const pillLabel = cardStatusPillLabel(status, {
    censusStatus: external.census_status,
    episodeEnd: formatDateToMMDDYYYY(external.census?.episode_end ?? null),
  });
  const text = variant === 'page' ? 'text-sm' : 'text-xs';
  const isImportedChart = external.state === 'imported';
  // The status read says when the agency's import is switched off, even if
  // this page loaded while it was on. The status still shows; Import, Retry,
  // Refresh and Cancel would be refused, so none shows.
  const mayAct = canStart && live.can_start !== false;
  const startable = mayAct && !isImportedChart && canStartFromStatus(status);
  const refreshable = mayAct && isImportedChart && canRefreshFromStatus(status);
  const actionAllowed = !blockedReason && !startBlocked;
  // The server refuses a refresh while the earlier episodes are arriving.
  const refreshBlockedReason =
    blockedReason ??
    (isImportedChart && historyPending(live)
      ? HISTORY_ARRIVING_REFRESH_LINE
      : null);
  const showStart = startable && actionAllowed;
  const showRefresh = refreshable && !refreshBlockedReason && !startBlocked;
  // Said in place of the action a viewer could take, never to one who cannot.
  const shownBlockedReason = startable
    ? blockedReason
    : refreshable
      ? refreshBlockedReason
      : null;
  // Cancel while queued or running. A running import's child is canceled and
  // the queue settles the row as canceled.
  const showCancel = mayAct && isMovingStatus(status);
  const showRecert =
    status === 'imported' && (recertHref || onAddRecertEpisode);
  // A finished refresh is shown for a while, then fades; the Import report
  // keeps every run (Daniel, 2026-10-09: "Fade from view, keep in records").
  const refreshResult = completedRefreshResult(live, Date.now());
  const keptMoreLine = refreshResult
    ? keptLinesMoreLine(
        refreshResult.kept_lines?.length ?? 0,
        refreshResult.kept_lines_total ?? 0
      )
    : null;
  // Clicks inside a card must not open the chart underneath.
  const stop = (event: React.SyntheticEvent) => event.stopPropagation();
  const inline = layout === 'inline';
  const fullLine = inline ? 'basis-full' : '';
  // The chart bar draws its own Imported pill. Queued and Refreshing stay
  // beside it; a finished refresh does not add the green status pill there.
  const showPill =
    status !== 'imported' ||
    showImportedPill ||
    (refreshResult !== null && !inline);
  // Chart bar only: a shown refresh names itself. Otherwise the previous or
  // faded refresh, or the first import when this chart was never refreshed.
  const fadedRefreshAt =
    live.refresh && live.state === 'completed' ? live.finished_at : null;
  const priorRefreshOn =
    inline && refreshResult == null
      ? formatDateInAgencyZone(live.last_refreshed_at ?? fadedRefreshAt)
      : '';
  const importedOn =
    !inline || priorRefreshOn || refreshResult != null
      ? ''
      : formatDateInAgencyZone(importedAt ?? live.imported_at);
  const provenance = priorRefreshOn
    ? lastRefreshedLine(priorRefreshOn)
    : importedOn
      ? importedOnLine(importedOn)
      : '';
  const queueLine =
    status === 'queued' && (live.queue_position ?? 0) > 0
      ? queuePositionLine(live.queue_position!)
      : '';
  const queueAndRefresh = [provenance, queueLine].filter(Boolean).join(' ');
  const shortRefresh = inline && (refreshResult != null || provenance !== '');

  return (
    <div
      className={inline ? 'contents' : 'space-y-1'}
      onClick={stop}
      data-testid='external-emr-import-controls'
    >
      {showPill && (
        <div className='flex flex-wrap items-center gap-1.5'>
          <ExternalEmrStatusPill status={status} label={pillLabel} />
          {running && (
            <span
              className={`${text} text-gray-700`}
              data-testid='import-stage'
            >
              {importStageLabel(live)}
            </span>
          )}
        </div>
      )}

      {timeLeft && (
        <p className={`${text} text-gray-700`} data-testid='import-time-left'>
          {timeLeft}
        </p>
      )}

      {queueAndRefresh && (
        <p
          className={`${text} text-gray-700`}
          data-testid={queueLine ? 'queue-position' : 'last-refreshed'}
        >
          {queueAndRefresh}
        </p>
      )}

      {status === 'failed' && (
        // The refusal is written for users (what to fix and where), as on the
        // intake import card.
        <p
          className={`${text} ${fullLine} text-red-700`}
          data-testid='import-refusal'
        >
          {live.refusal?.message || IMPORT_FAILED_FALLBACK}
        </p>
      )}

      {status === 'refresh_failed' && (
        <p
          className={`${text} ${fullLine} text-red-700`}
          data-testid='refresh-refusal'
        >
          {live.refusal?.message || REFRESH_FAILED_FALLBACK}
        </p>
      )}

      {refreshResult && (
        <div
          className={`${text} ${inline ? '' : 'space-y-0.5'} text-gray-700`}
          data-testid='refresh-result'
        >
          <p>
            {refreshResultLine(
              refreshResult,
              formatDateInAgencyZone(live.finished_at)
            )}
          </p>
          {(refreshResult.kept_lines ?? []).map(line => (
            <p key={line} className='text-muted-foreground'>
              {line}
            </p>
          ))}
          {keptMoreLine && (
            <p className='text-muted-foreground'>{keptMoreLine}</p>
          )}
        </div>
      )}

      {shownBlockedReason && !startBlocked && (
        <p
          className={`${text} ${fullLine} text-gray-700`}
          data-testid='external-emr-import-blocked'
        >
          {shownBlockedReason}
        </p>
      )}

      {(showStart || showRefresh || showCancel || showRecert) && (
        <div className={inline ? 'contents' : 'flex flex-wrap gap-2 pt-1'}>
          {showStart && (
            <Button
              size='sm'
              variant={status === 'failed' ? 'outline' : 'default'}
              disabled={busy}
              onClick={requestStart}
              data-testid='external-emr-start-import'
            >
              {busy && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
              {status === 'failed' ? RETRY_IMPORT_LABEL : START_IMPORT_LABEL}
            </Button>
          )}
          {showRefresh && (
            <Button
              size='sm'
              variant='outline'
              disabled={busy}
              onClick={() => void refresh()}
              title={REFRESH_CHART_HELP}
              data-testid='external-emr-refresh-chart'
            >
              {busy ? (
                <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
              ) : (
                shortRefresh && <RefreshCw className='mr-1.5 h-3.5 w-3.5' />
              )}
              {shortRefresh ? REFRESH_CHART_SHORT_LABEL : REFRESH_CHART_LABEL}
            </Button>
          )}
          {showCancel && (
            <Button
              size='sm'
              variant='outline'
              disabled={busy}
              onClick={() => void cancel()}
              data-testid='external-emr-cancel-import'
            >
              {CANCEL_IMPORT_LABEL}
            </Button>
          )}
          {showRecert &&
            (onAddRecertEpisode ? (
              <Button
                size='sm'
                onClick={onAddRecertEpisode}
                data-testid='add-recert-episode'
              >
                {ADD_RECERT_EPISODE_LABEL}
              </Button>
            ) : (
              <Link
                to={recertHref!}
                className={`${text} font-medium text-primary underline underline-offset-2`}
                data-testid='add-recert-episode'
              >
                {ADD_RECERT_EPISODE_LABEL}
              </Link>
            ))}
        </div>
      )}

      <Dialog
        open={confirmOpen}
        onOpenChange={open => {
          if (!open && !busy) setConfirmOpen(false);
        }}
      >
        <DialogContent className='w-[calc(100vw-2rem)] max-w-md' onClick={stop}>
          <DialogHeader>
            <DialogTitle>
              {cutover ? CUTOVER_CONFIRM_TITLE : MID_EPISODE_CONFIRM_TITLE}
            </DialogTitle>
            <DialogDescription>
              {cutover
                ? CUTOVER_CONFIRM_BODY
                : midEpisodeConfirmBody(takeOverV2)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className='flex flex-col-reverse gap-2 sm:flex-row'>
            <Button
              variant='outline'
              disabled={busy}
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={busy}
              onClick={async () => {
                await start();
                setConfirmOpen(false);
              }}
              data-testid='confirm-mid-episode-import'
            >
              {busy && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              {CONFIRM_START_ACCEPT}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
